import { useEffect, useMemo, useState } from "react";
import { useTitleBar } from "@/context/TitleBarProvider";
import { useParams, Link } from "react-router-dom";
import {
  Table,
  Lock,
  Trash2,
  EllipsisIcon,
  CodeIcon,
  LayoutDashboard,
} from "lucide-react";
import { useWorkspaceByIdQuery } from "@/hooks/useWorkspacesQuery";
import { useAutomations } from "@/context/AutomationsProvider";
import ProcessListSection from "@/components/processes/ProcessListSection";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { WorkspaceAccessDialog } from "@/components/workspaces/WorkspaceAccessDialog";
import AutomateProcessButton from "@/components/processes/AutomateProcessButton";
import { DeleteWorkspaceModal } from "@/components/workspaces/DeleteWorkspaceModal";
import DashboardFrame from "@/components/workspaces/DashboardFrame";

const WorkspaceDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { data: workspaceData, isLoading } = useWorkspaceByIdQuery(id);
  const { workspaces, isLoading: automationsLoading } = useAutomations();
  const workspacePipelines = useMemo(
    () => workspaces[workspaceData?.id ?? ""]?.pipelines ?? [],
    [workspaces, workspaceData]
  );
  const workspaceProcesses = useMemo(
    () => Object.values(workspaces[workspaceData?.id ?? ""]?.processes ?? {}),
    [workspaces, workspaceData]
  );
  const { setTitle, setIcon, setButtons } = useTitleBar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [dashboardOpen, setDashboardOpen] = useState(false);

  const dashboardUrl = useMemo(() => {
    if (!workspaceData?.editor_url) return null;
    return workspaceData.editor_url.replace("-editor.", "-dashboard.");
  }, [workspaceData?.editor_url]);

  useEffect(() => {
    setTitle(workspaceData?.name ?? "Workspace");
    setIcon(<Table size={24} />);
    setButtons(
      <>
        <Button variant="outline" onClick={() => setDialogOpen(true)}>
          <Lock size={16} />
          User access
        </Button>
        {workspaceProcesses.length > 0 || workspacePipelines.length > 0 ? (
          <AutomateProcessButton
            workspaceId={workspaceData?.id}
            automationServerId={workspaceData?.automation_server}
          />
        ) : null}
        {dashboardUrl ? (
          <Button
            variant={dashboardOpen ? "outline" : "default"}
            onClick={() => setDashboardOpen((v) => !v)}
          >
            <LayoutDashboard size={16} />
            {dashboardOpen ? "Close Dashboard" : "Open Dashboard"}
          </Button>
        ) : null}
        <Link to={workspaceData?.editor_url ?? ""} target="_blank">
          <Button>
            <CodeIcon size={16} /> Code
          </Button>
        </Link>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              aria-label="Workspace actions menu"
              data-testid="workspace-actions-menu"
            >
              <EllipsisIcon size={16} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setDeleteModalOpen(true)}
              data-testid="delete-workspace-menu-item"
            >
              <Trash2 size={16} className="mr-2" />
              Delete Workspace
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );
  }, [
    setTitle,
    setIcon,
    setButtons,
    workspaceData,
    workspaceProcesses,
    workspacePipelines,
    dashboardOpen,
    dashboardUrl,
  ]);

  if (isLoading || automationsLoading) {
    return <div>Loading...</div>;
  }

  if (!workspaceData) {
    return <div>Workspace not found</div>;
  }

  return (
    <>
      {dashboardOpen && dashboardUrl ? (
        <DashboardFrame
          url={dashboardUrl}
          onClose={() => setDashboardOpen(false)}
        />
      ) : workspaceProcesses.length > 0 || workspacePipelines.length > 0 ? (
        <ProcessListSection
          automations={workspacePipelines}
          processes={workspaceProcesses}
          isLoading={automationsLoading}
          hideWorkspaceColumn={true}
          lastUpdated={workspaceData.updated_at}
        />
      ) : (
        <div className="flex justify-center items-center h-40 border border-dashed m-4">
          <AutomateProcessButton
            workspaceId={workspaceData?.id}
            automationServerId={workspaceData?.automation_server}
          />
        </div>
      )}
      <WorkspaceAccessDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        workspaceId={workspaceData.id}
      />
      <DeleteWorkspaceModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        automationServerId={workspaceData?.automation_server ?? ""}
        workspaceId={workspaceData?.id ?? ""}
        workspaceName={workspaceData?.name ?? ""}
      />
    </>
  );
};

export default WorkspaceDetailPage;
